import { useEffect, useMemo, useRef } from 'react';
import { MatchState } from '../../../../types';
import { useTimer, useMatchLogic } from '../../../../hooks';

interface GameFlowHookReturn {
  // Timers
  matchDuration: number;
  gameDuration: number;
  
  // Logique du match
  matchLogic: any; // Type à ajuster selon votre useMatchLogic
  
  // États calculés
  canMakeDecision: boolean;
  setsToWin: number;
  isMatchFinished: boolean;
  
  // Utilitaires
  formatTime: (seconds: number) => string;
  
  // Callbacks pour les effets
  onGameEnd?: (winnerName: string) => void;
}

export interface GameEndInfo {
  winnerName: string;
  loserName: string;
  winnerScore: number;
  loserScore: number;
  winnerGames: number;
  loserGames: number;
}

export interface MatchEndInfo {
  winnerName: string;
  loserName: string;
  winnerGames: number;
  loserGames: number;
  winner: 'A' | 'B';
}

interface UseGameFlowProps {
  matchState: MatchState;
  onGameEnd?: (info: GameEndInfo) => void;
  onMatchEnd?: (info: MatchEndInfo) => void;
}

export const useGameFlow = ({
  matchState,
  onGameEnd,
  onMatchEnd
}: UseGameFlowProps): GameFlowHookReturn => {
  
  // Hooks existants
  const matchLogic = useMatchLogic(matchState);
  const matchDuration = useTimer(matchState.matchStartTime);
  const gameDuration = useTimer(matchState.gameStartTime);
  
  // Calculs dérivés
  const setsToWin = useMemo(() => {
    return matchState.matchInfo.scoringType === 'BestOf5' ? 3 : 2;
  }, [matchState.matchInfo.scoringType]);
  
  const isMatchFinished = useMemo(() => {
    return matchState.setScores.A >= setsToWin || matchState.setScores.B >= setsToWin;
  }, [matchState.setScores, setsToWin]);
  
  const canMakeDecision = useMemo(() => {
    return true; // Toujours possible, côté sera assigné par défaut si non sélectionné
  }, []);
  
  // Utilitaires
  const formatTime = (seconds: number): string => {
    if (seconds < 0) return "00:00"; 
    
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  const prevGameResultsLength = useRef(matchState.gameResults.length);
  
  // Effet : Détecter la fin d'un jeu et déclencher le callback
    useEffect(() => {
    const currentLength = matchState.gameResults.length;

    // Seulement si un nouveau jeu a été ajouté (pas rollback)
    if (currentLength > prevGameResultsLength.current && currentLength > 0 && !isMatchFinished) {
        const lastGame = matchState.gameResults[currentLength - 1];
        const winnerName = lastGame.winner === 'A'
        ? matchState.matchInfo.playerA
        : matchState.matchInfo.playerB;
        const loserName = lastGame.winner === 'A'
        ? matchState.matchInfo.playerB
        : matchState.matchInfo.playerA;

        // Calculer les scores du jeu et le nombre de jeux gagnés
        const winnerScore = lastGame.winner === 'A' ? lastGame.scoreA : lastGame.scoreB;
        const loserScore = lastGame.winner === 'A' ? lastGame.scoreB : lastGame.scoreA;
        const winnerGames = matchState.setScores[lastGame.winner];
        const loserGames = matchState.setScores[lastGame.winner === 'A' ? 'B' : 'A'];

        if (onGameEnd) {
        onGameEnd({
            winnerName,
            loserName,
            winnerScore,
            loserScore,
            winnerGames,
            loserGames,
        });
        }
    }

    prevGameResultsLength.current = currentLength;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
    matchState.gameResults.length,
    matchState.matchInfo.playerA,
    matchState.matchInfo.playerB,
    matchState.setScores,
    isMatchFinished
    // onGameEnd omis intentionnellement pour éviter les boucles infinies
    ]);

  // Effet : Détecter la fin de match et déclencher le callback onMatchEnd
  const prevPendingMatchEnd = useRef(matchState.pendingMatchEnd);

  useEffect(() => {
    // Détecter quand pendingMatchEnd passe de false à true
    if (matchState.pendingMatchEnd && !prevPendingMatchEnd.current && matchState.winner) {
      const winner = matchState.winner;
      const winnerName = winner === 'A'
        ? matchState.matchInfo.playerA
        : matchState.matchInfo.playerB;
      const loserName = winner === 'A'
        ? matchState.matchInfo.playerB
        : matchState.matchInfo.playerA;
      const winnerGames = matchState.setScores[winner];
      const loserGames = matchState.setScores[winner === 'A' ? 'B' : 'A'];

      if (onMatchEnd) {
        onMatchEnd({
          winnerName,
          loserName,
          winnerGames,
          loserGames,
          winner,
        });
      }
    }

    prevPendingMatchEnd.current = matchState.pendingMatchEnd;
  }, [
    matchState.pendingMatchEnd,
    matchState.winner,
    matchState.matchInfo.playerA,
    matchState.matchInfo.playerB,
    matchState.setScores,
    onMatchEnd
  ]);

  return {
    // Timers
    matchDuration,
    gameDuration,
    
    // Logique du match
    matchLogic,
    
    // États calculés
    canMakeDecision,
    setsToWin,
    isMatchFinished,
    
    // Utilitaires
    formatTime
  };
};